import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import CookieConsent from "./components/CookieConsent";
import TipBox from "./components/TipBox";
import SocialProofToast from "./components/SocialProofToast";
import { ThemeProvider } from "./contexts/ThemeContext";
import Home from "./pages/Home";
import { lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";

const Dashboard = lazy(() => import("./pages/Dashboard"));
const Practice = lazy(() => import("./pages/Practice"));
const ScenarioBriefing = lazy(() => import("./pages/ScenarioBriefing"));
const RoleplaySession = lazy(() => import("./pages/RoleplaySession"));
const SessionResults = lazy(() => import("./pages/SessionResults"));
const History = lazy(() => import("./pages/History"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const Profile = lazy(() => import("./pages/Profile"));
const TeamPage = lazy(() => import("./pages/TeamPage"));
const Upgrade = lazy(() => import("./pages/Upgrade"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const HealthCheck = lazy(() => import("./pages/HealthCheck"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const LeadMagnet = lazy(() => import("./pages/LeadMagnet"));
const Referral = lazy(() => import("./pages/Referral"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Testimonials = lazy(() => import("./pages/Testimonials"));
const Tip = lazy(() => import("./pages/Tip"));
const GuestPractice = lazy(() => import("./pages/GuestPractice"));
const ChatBubble = lazy(() => import("./components/ChatBubble"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

function Router() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/pricing" component={Pricing} />
        <Route path="/dashboard" component={Dashboard} />
        <Route path="/practice" component={Practice} />
        <Route path="/practice/:scenarioId" component={ScenarioBriefing} />
        <Route path="/session/:sessionId" component={RoleplaySession} />
        <Route path="/session/:sessionId/results" component={SessionResults} />
        <Route path="/history" component={History} />
        <Route path="/leaderboard" component={Leaderboard} />
        <Route path="/profile" component={Profile} />
        <Route path="/team" component={TeamPage} />
        <Route path="/upgrade" component={Upgrade} />
        <Route path="/admin" component={AdminDashboard} />
        <Route path="/admin/health" component={HealthCheck} />
        <Route path="/privacy" component={Privacy} />
        <Route path="/terms" component={Terms} />
        <Route path="/resources" component={LeadMagnet} />
        <Route path="/referral" component={Referral} />
        <Route path="/blog" component={Blog} />
        <Route path="/blog/:slug" component={BlogPost} />
        <Route path="/testimonials" component={Testimonials} />
        <Route path="/tip" component={Tip} />
        <Route path="/guest-practice" component={GuestPractice} />
        <Route path="/404" component={NotFound} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="dark">
        <TooltipProvider>
          <Toaster />
          <Router />
          <TipBox />
          <Suspense fallback={null}><ChatBubble /></Suspense>
          <SocialProofToast />
          <CookieConsent />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
